import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import StructuredRecipeDisplay from '../components/StructuredRecipeDisplay';
import api from '../api/api';
import { trackABTestEvent } from '../utils/abTesting';
import '../styles/tsumugi-design.css';
import '../styles/pages/FridgeRecipeResultPage.css';

/**
 * FridgeRecipeResultPage - 冷蔵庫写真から認識した食材でレシピ生成
 * つむぎデザインシステム準拠
 *
 * フロー:
 * 1. localStorageから認識済み食材を取得
 * 2. /api/fridge/recipe でレシピ生成
 * 3. 結果を表示
 * 4. 自動保存
 */
const FridgeRecipeResultPage = () => {
  const { user, plan } = useAuth();
  const navigate = useNavigate();

  const [ingredients, setIngredients] = useState([]);
  const [isGenerating, setIsGenerating] = useState(true);
  const [generatedRecipe, setGeneratedRecipe] = useState('');
  const [error, setError] = useState(null);
  const [recipeDifficulty, setRecipeDifficulty] = useState('easy'); // デフォルトは簡単レシピ

  // ページタイトル設定
  useEffect(() => {
    document.title = 'レシピ結果 | つむぎ';
  }, []);

  /**
   * 初期化: localStorageから食材取得 → レシピ生成
   */
  useEffect(() => {
    const fridgeIngredientsStr = localStorage.getItem('fridgeIngredients');

    if (!fridgeIngredientsStr) {
      // 食材がない場合は元のページに戻る
      navigate('/fridge-photo');
      return;
    }

    try {
      const parsedIngredients = JSON.parse(fridgeIngredientsStr);
      setIngredients(parsedIngredients);

      // レシピ生成開始
      generateRecipe(parsedIngredients);
    } catch (err) {
      console.error('Failed to parse ingredients:', err);
      navigate('/fridge-photo');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  /**
   * レシピ生成API呼び出し
   */
  const generateRecipe = async (ingredientList) => {
    if (!ingredientList || ingredientList.length === 0) {
      setError('食材が選択されていません');
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    setError(null);

    // Track start
    if (user?._id) {
      trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_generation_start', {
        ingredientCount: ingredientList.length,
        method: 'fridge_photo',
        userPlan: plan
      });
    }

    try {
      // アレルギー・食事制限を取得
      const restrictions = [];
      if (user?.profile?.allergies && user.profile.allergies.length > 0) {
        restrictions.push('アレルギー: ' + user.profile.allergies.join('、'));
      }
      if (user?.profile?.allergiesOther) {
        restrictions.push('アレルギー（その他）: ' + user.profile.allergiesOther);
      }
      if (user?.profile?.avoidFoods && user.profile.avoidFoods.length > 0) {
        restrictions.push('避けたい食材: ' + user.profile.avoidFoods.join('、'));
      }
      if (user?.profile?.avoidFoodsOther) {
        restrictions.push('避けたい食材（その他）: ' + user.profile.avoidFoodsOther);
      }

      const response = await api.post('/api/fridge/recipe', {
        ingredients: ingredientList,
        userSeasonings: [], // 将来的に調味料設定を統合
        dietaryRestrictions: restrictions.join('、'),
        recipeDifficulty: recipeDifficulty
      });

      if (response.data.success) {
        setGeneratedRecipe(response.data.recipe);

        // Track success
        if (user?._id) {
          trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_generation_complete', {
            ingredientCount: ingredientList.length,
            method: 'fridge_photo',
            success: true,
            userPlan: plan
          });
        }

        // 自動保存
        await saveGeneratedRecipe(response.data.recipe);
      } else {
        throw new Error(response.data.message || 'レシピの生成に失敗しました');
      }
    } catch (error) {
      console.error('Recipe generation failed:', error);

      // Track failure
      if (user?._id) {
        trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_generation_complete', {
          ingredientCount: ingredientList.length,
          method: 'fridge_photo',
          success: false,
          error: error.response?.status === 429 ? 'quota_exceeded' : 'generation_failed',
          userPlan: plan
        });
      }

      if (error.response?.status === 429) {
        setError('今月は上限までご利用いただきました。来月またご利用いただけます。');
      } else {
        setError(error.response?.data?.message || 'レシピの生成に失敗しました。もう一度お試しください。');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * レシピを自動保存
   */
  const saveGeneratedRecipe = async (recipeMarkdown) => {
    try {
      // タイトルを抽出（## 料理名 の形式）
      const titleMatch = recipeMarkdown.match(/##\s+(.+)/);
      const title = titleMatch ? titleMatch[1].trim() : '冷蔵庫レシピ';

      // タグ自動抽出
      const autoTags = ['冷蔵庫レシピ'];
      const lowerRecipe = recipeMarkdown.toLowerCase();
      if (lowerRecipe.includes('簡単') || lowerRecipe.includes('時短')) autoTags.push('簡単');

      // 保存API呼び出し
      await api.post('/api/recipes/save', {
        title,
        content: recipeMarkdown.trim(),
        sourceConsultationId: null,
        tags: autoTags
      });

      console.log('✅ レシピを保存しました:', title);
    } catch (error) {
      console.error('レシピ保存エラー:', error);
      // 保存失敗してもエラー表示はしない（ユーザー体験を損ねない）
    }
  };

  /**
   * 別のレシピを生成
   */
  const handleRegenerate = () => {
    generateRecipe(ingredients);
  };

  /**
   * 最初からやり直す
   */
  const handleStartOver = () => {
    localStorage.removeItem('fridgeIngredients');
    navigate('/fridge-photo');
  };

  return (
    <AppLayout>
      <PageTitle
        title="レシピ生成結果"
        subtitle="つむぎが考えた献立です"
        icon="🍳"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="px-3 py-2">
        {/* ローディング中 */}
        {isGenerating && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-white/95 backdrop-blur-sm flex items-center justify-center z-[9999]">
            <div className="bg-gradient-to-br from-mint/10 to-honey/10 border-2 border-mint/30 p-12 px-14 rounded-3xl text-center shadow-2xl max-w-md">
              {/* つむぎ画像 */}
              <div className="mb-4 flex justify-center">
                <img
                  src="/tsumugi.png"
                  alt="つむぎ"
                  className="fridge-recipe-result-page__tsumugi-image animate-bounce"
                />
              </div>
              <div className="w-[60px] h-[60px] border-[5px] border-gray-200 border-t-mint border-r-honey rounded-full mx-auto mb-6 animate-spin"></div>
              <p className="text-xl font-semibold text-gray-800 mb-3 tracking-wide">
                つむぎがレシピを考えています...
              </p>
              <p className="text-[0.95rem] text-gray-600 mb-6 leading-relaxed">
                栄養バランスを考えながら、美味しい献立を作っています
              </p>
              <div className="flex justify-center gap-2">
                <span className="w-2.5 h-2.5 bg-mint rounded-full animate-bounce"></span>
                <span className="w-2.5 h-2.5 bg-honey rounded-full animate-bounce delay-75"></span>
                <span className="w-2.5 h-2.5 bg-peach rounded-full animate-bounce delay-150"></span>
              </div>
            </div>
          </div>
        )}

        {/* エラー表示 */}
        {error && !isGenerating && (
          <div className="tsumugi-card tsumugi-card--shell mb-6">
            <div className="flex items-start gap-4">
              <div className="text-4xl">❌</div>
              <div className="flex-1">
                <h3 className="tsumugi-card-title text-red-700 mb-2">エラー</h3>
                <p className="text-sm text-gray-700 mb-4">{error}</p>
                <div className="flex gap-3">
                  <button
                    className="tsumugi-btn tsumugi-btn-primary"
                    onClick={handleRegenerate}
                  >
                    もう一度試す
                  </button>
                  <button
                    className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray"
                    onClick={handleStartOver}
                  >
                    最初からやり直す
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* レシピ表示 */}
        {generatedRecipe && !isGenerating && (
          <>
            {/* レシピの詳しさ選択 */}
            <div className="tsumugi-card tsumugi-card--shell mb-3">
              <h3 className="tsumugi-card-title mb-2">
                🎚️ レシピの詳しさ <span className="text-xs text-gray-600">（橋本病患者向け）</span>
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                倦怠感に応じて、レシピの詳しさを選べます
              </p>
              <div className="flex gap-2 mb-2">
                <button
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    recipeDifficulty === 'super_easy'
                      ? 'bg-mint text-white border-2 border-mint'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-mint'
                  }`}
                  onClick={() => {
                    setRecipeDifficulty('super_easy');
                    generateRecipe(ingredients);
                  }}
                >
                  <div className="text-xs mb-0.5">とにかく簡単</div>
                  <div className="text-[0.65rem] opacity-80">5-10分</div>
                </button>
                <button
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    recipeDifficulty === 'easy'
                      ? 'bg-mint text-white border-2 border-mint'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-mint'
                  }`}
                  onClick={() => {
                    setRecipeDifficulty('easy');
                    generateRecipe(ingredients);
                  }}
                >
                  <div className="text-xs mb-0.5">簡単 <span className="text-[0.6rem]">おすすめ</span></div>
                  <div className="text-[0.65rem] opacity-80">15-20分</div>
                </button>
                <button
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    recipeDifficulty === 'detailed'
                      ? 'bg-mint text-white border-2 border-mint'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-mint'
                  }`}
                  onClick={() => {
                    setRecipeDifficulty('detailed');
                    generateRecipe(ingredients);
                  }}
                >
                  <div className="text-xs mb-0.5">こだわり</div>
                  <div className="text-[0.65rem] opacity-80">30分以上</div>
                </button>
              </div>
            </div>

            {/* 使用した食材 */}
            <div className="tsumugi-card tsumugi-card--mint mb-3">
              <h3 className="tsumugi-card-title mb-2">📝 使用した食材</h3>
              <div className="flex flex-wrap gap-2">
                {ingredients.map((ingredient, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-1 bg-white border border-mint rounded-full text-sm font-semibold text-gray-700"
                  >
                    {ingredient}
                  </span>
                ))}
              </div>
            </div>

            {/* レシピ本体 */}
            <div className="mb-3">
              <StructuredRecipeDisplay recipeMarkdown={generatedRecipe} />
            </div>

            {/* アクションボタン */}
            <div className="flex gap-2 mb-3">
              <button
                className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint flex-1"
                onClick={handleRegenerate}
              >
                🔄 別のレシピを生成
              </button>
              <button
                className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray flex-1"
                onClick={handleStartOver}
              >
                最初からやり直す
              </button>
            </div>

            {/* ヒント */}
            <div className="tsumugi-card tsumugi-card--shell">
              <div className="flex items-start gap-2">
                <div className="text-xl">💡</div>
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>レシピは自動保存されました</strong>
                  </p>
                  <p className="text-xs text-gray-600">
                    マイページの「保存したレシピ」からいつでも見返せます
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default FridgeRecipeResultPage;
